import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import {
  Truck,
  AlertTriangle,
  Clock,
  Package,
  RefreshCw,
  Users,
  Calendar,
  CheckCircle,
  XCircle,
  TrendingUp
} from 'lucide-react';
import { formatDate } from '../lib/utils';

const OperationsDashboard = () => {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({
    equipmentInTransit: [],
    bomberazosToday: [],
    loansExpiringSoon: [],
    overdueLoans: [],
    recentReturns: [],
    stats: {
      totalInTransit: 0,
      totalBomberazosToday: 0,
      totalExpiringSoon: 0,
      totalOverdue: 0
    }
  });
  const [lastUpdate, setLastUpdate] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      // Get dashboard KPIs, active loans and bomberazos in parallel
      const [kpisRes, activeLoansRes, bomberazosRes, allMovementsRes] = await Promise.all([
        api().get('/dashboard/kpis').catch(() => ({ data: {} })),
        api().get('/dashboard/active-loans').catch(() => ({ data: [] })),
        api().get('/audit/bomberazos?days=1').catch(() => ({ data: { movements: [] } })),
        api().get('/movements').catch(() => ({ data: [] }))
      ]);

      const kpis = kpisRes.data || {};
      const activeLoans = activeLoansRes.data || [];
      const allMovements = allMovementsRes.data || [];
      
      // Filter today's bomberazos
      const today = new Date().toISOString().split('T')[0];
      const bomberazosToday = (bomberazosRes.data?.movements || []).filter(m => 
        m.date?.startsWith(today)
      );

      // Get equipment in transit: active salidas + prestamos without return
      const equipmentInTransit = allMovements.filter(m => 
        (m.type === 'salida' || m.type === 'prestamo') && 
        !m.has_return && 
        !m.is_deleted
      );

      // Process loans for expiring and overdue
      const now = new Date();
      const in7Days = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);

      const loansExpiring = activeLoans.filter(loan => {
        if (!loan.loan_due_date) return false;
        const dueDate = new Date(loan.loan_due_date);
        return dueDate > now && dueDate <= in7Days;
      });

      const overdueLoans = activeLoans.filter(loan => {
        if (!loan.loan_due_date) return false;
        const dueDate = new Date(loan.loan_due_date);
        return dueDate < now;
      });

      // Calculate totals
      const totalInTransit = equipmentInTransit.length;

      setData({
        equipmentInTransit: equipmentInTransit.slice(0, 10),
        bomberazosToday: bomberazosToday.slice(0, 5),
        loansExpiringSoon: loansExpiring.slice(0, 5),
        overdueLoans: overdueLoans.slice(0, 5),
        stats: {
          totalInTransit: totalInTransit,
          totalBomberazosToday: bomberazosToday.length,
          totalExpiringSoon: loansExpiring.length,
          totalOverdue: overdueLoans.length
        }
      });
      setLastUpdate(new Date());
    } catch (error) {
      console.error('Error loading operations data:', error);
      toast.error('Error al cargar datos operativos');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    loadData();
    // Refresh every 2 minutes
    const interval = setInterval(loadData, 120000);
    return () => clearInterval(interval);
  }, [loadData]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'critical': return 'bg-red-500';
      case 'warning': return 'bg-amber-500';
      case 'good': return 'bg-emerald-500';
      default: return 'bg-gray-500';
    }
  };

  const getOverallStatus = () => {
    if (data.stats.totalOverdue > 0 || data.stats.totalBomberazosToday > 3) return 'critical';
    if (data.stats.totalExpiringSoon > 5 || data.stats.totalBomberazosToday > 0) return 'warning';
    return 'good';
  };

  const StatusIndicator = ({ status, label }) => (
    <div className="flex items-center gap-2">
      <div className={`w-3 h-3 rounded-full ${getStatusColor(status)} animate-pulse`} />
      <span className="text-sm font-medium">{label}</span>
    </div>
  );

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Dashboard Operativo</h1>
          <p className="text-muted-foreground">
            Vista en tiempo real de operaciones
            {lastUpdate && (
              <span className="ml-2 text-xs">
                Actualizado: {lastUpdate.toLocaleTimeString()}
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-4">
          <StatusIndicator 
            status={getOverallStatus()} 
            label={
              getOverallStatus() === 'critical' ? 'Requiere atención' :
              getOverallStatus() === 'warning' ? 'Revisar' : 'Todo en orden'
            }
          />
          <Button variant="outline" onClick={loadData} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Actualizar
          </Button>
        </div>
      </div>

      {/* Stats Cards - Semáforo */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className={`card-hover border-l-4 ${data.stats.totalInTransit > 0 ? 'border-l-blue-500' : 'border-l-gray-500'}`}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">En Tránsito</p>
                <p className="text-3xl font-bold text-blue-500">
                  {loading ? <Skeleton className="h-9 w-16" /> : data.stats.totalInTransit}
                </p>
              </div>
              <Truck className="h-8 w-8 text-blue-500/50" />
            </div>
          </CardContent>
        </Card>

        <Card className={`card-hover border-l-4 ${
          data.stats.totalBomberazosToday > 3 ? 'border-l-red-500' :
          data.stats.totalBomberazosToday > 0 ? 'border-l-amber-500' : 'border-l-emerald-500'
        }`}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Bomberazos Hoy</p>
                <p className={`text-3xl font-bold ${
                  data.stats.totalBomberazosToday > 3 ? 'text-red-500' :
                  data.stats.totalBomberazosToday > 0 ? 'text-amber-500' : 'text-emerald-500'
                }`}>
                  {loading ? <Skeleton className="h-9 w-16" /> : data.stats.totalBomberazosToday}
                </p>
              </div>
              <AlertTriangle className={`h-8 w-8 ${
                data.stats.totalBomberazosToday > 0 ? 'text-amber-500/50' : 'text-emerald-500/50'
              }`} />
            </div>
          </CardContent>
        </Card>

        <Card className={`card-hover border-l-4 ${
          data.stats.totalExpiringSoon > 5 ? 'border-l-amber-500' : 'border-l-blue-500'
        }`}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Por Vencer (7d)</p>
                <p className={`text-3xl font-bold ${
                  data.stats.totalExpiringSoon > 5 ? 'text-amber-500' : 'text-blue-500'
                }`}>
                  {loading ? <Skeleton className="h-9 w-16" /> : data.stats.totalExpiringSoon}
                </p>
              </div>
              <Clock className="h-8 w-8 text-amber-500/50" />
            </div>
          </CardContent>
        </Card>

        <Card className={`card-hover border-l-4 ${
          data.stats.totalOverdue > 0 ? 'border-l-red-500' : 'border-l-emerald-500'
        }`}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Vencidos</p>
                <p className={`text-3xl font-bold ${
                  data.stats.totalOverdue > 0 ? 'text-red-500' : 'text-emerald-500'
                }`}>
                  {loading ? <Skeleton className="h-9 w-16" /> : data.stats.totalOverdue}
                </p>
              </div>
              {data.stats.totalOverdue > 0 ? (
                <XCircle className="h-8 w-8 text-red-500/50" />
              ) : (
                <CheckCircle className="h-8 w-8 text-emerald-500/50" />
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Equipment in Transit */}
        <Card className="card-hover">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Truck className="h-5 w-5 text-blue-500" />
              Equipo en Tránsito
            </CardTitle>
            <CardDescription>Equipos fuera de inventario</CardDescription>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[280px]">
              {loading ? (
                <div className="space-y-2">
                  {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-16 w-full" />)}
                </div>
              ) : data.equipmentInTransit.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Package className="h-10 w-10 mx-auto mb-2 opacity-50" />
                  <p>No hay equipo en tránsito</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {data.equipmentInTransit.map((mov) => (
                    <div key={mov.id} className="p-3 bg-muted/30 rounded-lg">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-sm">{mov.event_name || mov.loan_reason || 'Sin descripción'}</p>
                          <p className="text-xs text-muted-foreground">
                            {mov.responsible_name} • {mov.items?.length || 0} equipos
                          </p>
                        </div>
                        <Badge className={mov.type === 'prestamo' ? 'bg-purple-500/20 text-purple-400' : 'bg-blue-500/20 text-blue-400'}>
                          {mov.type === 'prestamo' ? 'Préstamo' : 'Evento'}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Bomberazos Today */}
        <Card className={`card-hover ${data.stats.totalBomberazosToday > 0 ? 'border border-amber-500/30' : ''}`}>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <AlertTriangle className={`h-5 w-5 ${data.stats.totalBomberazosToday > 0 ? 'text-amber-500' : 'text-muted-foreground'}`} />
              Bomberazos Hoy
            </CardTitle>
            <CardDescription>Salidas sin revisión del día</CardDescription>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[280px]">
              {loading ? (
                <div className="space-y-2">
                  {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-16 w-full" />)}
                </div>
              ) : data.bomberazosToday.length === 0 ? (
                <div className="text-center py-8 text-emerald-500/80">
                  <CheckCircle className="h-10 w-10 mx-auto mb-2" />
                  <p className="font-medium">Sin bomberazos hoy</p>
                  <p className="text-xs text-muted-foreground">Todas las salidas fueron revisadas</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {data.bomberazosToday.map((mov) => (
                    <div key={mov.id} className="p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-sm text-amber-400">{mov.responsible_name}</p>
                          <p className="text-xs text-muted-foreground">
                            {formatDate(mov.date)} • {mov.items?.length || 0} equipos
                          </p>
                        </div>
                        <AlertTriangle className="h-4 w-4 text-amber-500" />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Loans Expiring Soon */}
        <Card className="card-hover">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Clock className="h-5 w-5 text-amber-500" />
              Préstamos por Vencer
            </CardTitle>
            <CardDescription>Próximos 7 días</CardDescription>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[280px]">
              {loading ? (
                <div className="space-y-2">
                  {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-16 w-full" />)}
                </div>
              ) : data.loansExpiringSoon.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Calendar className="h-10 w-10 mx-auto mb-2 opacity-50" />
                  <p>Sin préstamos por vencer</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {data.loansExpiringSoon.map((loan) => (
                    <div key={loan.id} className="p-3 bg-muted/30 rounded-lg">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-sm">{loan.responsible_name}</p>
                          <p className="text-xs text-muted-foreground">
                            Vence: {formatDate(loan.loan_due_date)}
                          </p>
                        </div>
                        <Badge className="bg-amber-500/20 text-amber-400">
                          {Math.ceil((new Date(loan.loan_due_date) - new Date()) / (1000 * 60 * 60 * 24))} días
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Overdue Loans */}
        <Card className={`card-hover ${data.stats.totalOverdue > 0 ? 'border border-red-500/30' : ''}`}>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <XCircle className={`h-5 w-5 ${data.stats.totalOverdue > 0 ? 'text-red-500' : 'text-muted-foreground'}`} />
              Préstamos Vencidos
            </CardTitle>
            <CardDescription>Requieren atención inmediata</CardDescription>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[280px]">
              {loading ? (
                <div className="space-y-2">
                  {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-16 w-full" />)}
                </div>
              ) : data.overdueLoans.length === 0 ? (
                <div className="text-center py-8 text-emerald-500/80">
                  <CheckCircle className="h-10 w-10 mx-auto mb-2" />
                  <p className="font-medium">Sin préstamos vencidos</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {data.overdueLoans.map((loan) => (
                    <div key={loan.id} className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-sm text-red-400">{loan.responsible_name}</p>
                          <p className="text-xs text-muted-foreground">
                            Venció: {formatDate(loan.loan_due_date)}
                          </p>
                        </div>
                        <Badge className="bg-red-500/20 text-red-400">
                          {Math.abs(Math.ceil((new Date(loan.loan_due_date) - new Date()) / (1000 * 60 * 60 * 24)))} días
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default OperationsDashboard;
